import { IObservableArray, makeAutoObservable, observable } from "mobx";
import {
  isLearningPathModuleResponseDTO,
  LearningPathModuleResponseApi,
} from "./LearningPathModule";
import { isLearningPathIdResponseDTO } from "./LearningPathId";
import { ModuleResponseApi } from "../Module/Module";
import {
  isLearningPathResourceResponseDTO,
  LearningPathResourceResponseApi,
} from "./LearningPathResource";
import { ResourceIdAndNameResponseApi } from "../Resource/Resource";

export interface LearningPathWithoutModulesResponseApi {
  learningPathId: string;
  learningPathName: string;
  learningPathResources: LearningPathResourceResponseApi[];
}

export let isLearningPathWithoutModulesResponseDTO = (
  variable: unknown
): variable is LearningPathWithoutModulesResponseApi => {
  return (
    isLearningPathIdResponseDTO(variable) &&
    "learningPathName" in variable &&
    typeof variable.learningPathName === "string" &&
    "learningPathResources" in variable &&
    Array.isArray(variable.learningPathResources) &&
    variable.learningPathResources.reduce(
      (doArrayConsistOfResources, resource: unknown) =>
        doArrayConsistOfResources &&
        isLearningPathResourceResponseDTO(resource),
      true
    )
  );
};

export interface LearningPathResponseApi {
  learningPathId: string;
  learningPathName: string;
  learningPathModules: LearningPathModuleResponseApi[];
  learningPathResources: LearningPathResourceResponseApi[];
}

export let isLearningPathResponseDTO = (
  variable: unknown
): variable is LearningPathResponseApi => {
  return (
    isLearningPathIdResponseDTO(variable) &&
    "learningPathName" in variable &&
    typeof variable.learningPathName === "string" &&
    "learningPathModules" in variable &&
    Array.isArray(variable.learningPathModules) &&
    variable.learningPathModules.reduce(
      (doArrayConsistOfModules, module: unknown) =>
        doArrayConsistOfModules && isLearningPathModuleResponseDTO(module),
      true
    ) &&
    "learningPathResources" in variable &&
    Array.isArray(variable.learningPathResources) &&
    variable.learningPathResources.reduce(
      (doArrayConsistOfResources, resource: unknown) =>
        doArrayConsistOfResources &&
        isLearningPathResourceResponseDTO(resource),
      true
    )
  );
};

export class EmptyLearningPathResponseImpl implements LearningPathResponseApi {
  learningPathId: string = "";
  learningPathName: string = "";
  learningPathModules: LearningPathModuleResponseApi[] = [];
  learningPathResources: LearningPathResourceResponseApi[] = [];
}

export class ObservableLearningPathPropImpl implements LearningPathResponseApi {
  learningPathId: string;
  learningPathName: string;
  learningPathModules: IObservableArray<LearningPathModuleResponseApi>;
  learningPathResources: IObservableArray<LearningPathResourceResponseApi>;

  constructor(learningPath: LearningPathResponseApi) {
    this.learningPathId = learningPath.learningPathId;
    this.learningPathName = learningPath.learningPathName;
    this.learningPathModules = observable.array(
      learningPath.learningPathModules
    );
    this.learningPathResources = observable.array(
      learningPath.learningPathResources
    );
    makeAutoObservable(this);
  }

  clear() {
    let lp = new EmptyLearningPathResponseImpl();
    this.learningPathId = lp.learningPathId;
    this.learningPathName = lp.learningPathName;
    this.learningPathModules.replace(lp.learningPathModules);
    this.learningPathResources.replace(lp.learningPathResources);
  }

  onChangeId = (learningPathId: string) => {
    this.learningPathId = learningPathId;
  };

  onChangeName = (learningPathName: string) => {
    this.learningPathName = learningPathName;
  };

  onChangeModules = (learningPathModules: LearningPathModuleResponseApi[]) => {
    this.learningPathModules.replace(learningPathModules);
  };

  onChangeResources = (
    learningPathResources: LearningPathResourceResponseApi[]
  ) => {
    this.learningPathResources.replace(learningPathResources);
  };

  pushModule = (module: ModuleResponseApi, order: number) => {
    this.learningPathModules.push({
      learningPathModuleId: "lpID" + this.learningPathId + "mID" + order,
      module: module,
      learningPathModuleOrder: order,
    });
  };

  pushResource(resource: ResourceIdAndNameResponseApi) {
    this.learningPathResources.push({
      learningPathResourceId:
        "lpID" + this.learningPathId + "rID" + resource.resourceId,
      resource: resource,
    });
  }
}
